import React, { useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, Download, FileText, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CSVImporter = ({ onImportComplete }) => {
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/loads/csv-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'load_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.info('Importing loads...');
      const response = await axios.post(`${API}/loads/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success(`Successfully imported ${response.data.imported} loads!`);
        if (response.data.failed > 0) {
          toast.warning(`${response.data.failed} rows failed to import`);
        }
        if (onImportComplete) {
          onImportComplete();
        }
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card className="glass-card" data-testid="csv-importer-card">
      <CardHeader>
        <CardTitle className="text-xl font-heading flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          Bulk Import Loads
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 rounded-lg bg-background/50 space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Step 1: Download Template</p>
              <p className="text-xs text-muted-foreground">
                Get the CSV template with required fields and sample data
              </p>
            </div>
          </div>
          <Button 
            onClick={handleDownloadTemplate}
            variant="outline"
            className="w-full"
            data-testid="download-template-button"
          >
            <Download className="w-4 h-4 mr-2" />
            Download CSV Template
          </Button>
        </div>

        <div className="p-4 rounded-lg bg-background/50 space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Step 2: Fill Data</p>
              <p className="text-xs text-muted-foreground">
                Add your load data following the template format
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-background/50 space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Step 3: Upload File</p>
              <p className="text-xs text-muted-foreground">
                Upload your completed CSV file to import loads
              </p>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="csv-file-input"
          />
          <Button 
            onClick={() => fileInputRef.current?.click()}
            className="w-full glow-button"
            data-testid="upload-csv-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload CSV File
          </Button>
        </div>

        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <p className="text-xs text-amber-500">
            <strong>Required fields:</strong> pickup_address, pickup_lat, pickup_lng, delivery_address, 
            delivery_lat, delivery_lng, weight, distance_miles, rate, commodity
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CSVImporter;
